/**
 * Shared line-items table used across Orders, Quotations and Invoices.
 * Supports: product picker + inline "+ Create new product…", HSN/SAC,
 * Qty, per-line Unit (from lookups), Price, GST %, per-line GST amount and
 * an amount column. Row delete.
 *
 * Design keeps *no* internal state — parent owns `items`; changes are
 * pushed up via `onChange(newItems)`. Selecting the "__quick_create__"
 * sentinel from the product picker calls `onQuickCreate(index)` so the
 * caller can open its QuickProductDialog and place the new product back
 * into the correct row.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Trash } from "@phosphor-icons/react";

const inr = (v) => "₹" + (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function ItemsTable({ items, products, units, onChange,
                                     disabled, onQuickCreate,
                                     showHSN = true, testidPrefix = "order-line" }) {
  const upd = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const rm  = (i) => onChange(items.filter((_, idx) => idx !== i));
  const pick = (i, pid) => {
    if (pid === "__quick_create__") return onQuickCreate?.(i);
    const p = products.find(x => x.id === pid);
    if (p) upd(i, { product_id: pid, name: p.name, price: p.price, gst: p.gst, unit: p.unit || "PCS" });
  };
  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-1">
          <div className="col-span-3">Product</div>
          {showHSN && <div className="col-span-2">HSN/SAC</div>}
          <div className="col-span-1">Qty</div>
          <div className="col-span-2">Unit</div>
          <div className="col-span-1">Price&nbsp;₹</div>
          <div className={showHSN ? "col-span-1" : "col-span-2"}>GST %</div>
          <div className={showHSN ? "col-span-1" : "col-span-2"}>Amount&nbsp;₹</div>
        </div>
      )}
      {items.map((it, i) => {
        const lineAmount = (Number(it.qty) || 0) * (Number(it.price) || 0);
        return (
        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`${testidPrefix}-${i}`}>
          <div className="col-span-3">
            <Select value={it.product_id} onValueChange={(v) => pick(i, v)} disabled={disabled}>
              <SelectTrigger className="h-9" data-testid={`${testidPrefix}-product-${i}`}>
                <SelectValue placeholder={it.name || "Product"} />
              </SelectTrigger>
              <SelectContent>
                {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                {onQuickCreate && (
                  <SelectItem value="__quick_create__" data-testid={`${testidPrefix}-quick-create-${i}`}>
                    + Create new product…
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {showHSN && (
            <div className="col-span-2">
              <Input className="h-9" placeholder="HSN/SAC" value={it.hsn || ""}
                onChange={(e) => upd(i, { hsn: e.target.value })} disabled={disabled} />
            </div>
          )}
          <div className="col-span-1">
            <Input className="h-9" type="number" value={it.qty}
              onChange={(e) => upd(i, { qty: +e.target.value })} disabled={disabled}
              data-testid={`${testidPrefix}-qty-${i}`} />
          </div>
          <div className="col-span-2">
            <Select value={it.unit || "PCS"} onValueChange={(v) => upd(i, { unit: v })} disabled={disabled}>
              <SelectTrigger className="h-9" data-testid={`${testidPrefix}-unit-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {units.map(u => <SelectItem key={u.key} value={u.label}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Input className="h-9" type="number" value={it.price}
              onChange={(e) => upd(i, { price: +e.target.value })} disabled={disabled}
              data-testid={`${testidPrefix}-price-${i}`} />
          </div>
          <div className={showHSN ? "col-span-1" : "col-span-2"}>
            <Input className="h-9" type="number" value={it.gst}
              onChange={(e) => upd(i, { gst: +e.target.value })} placeholder="GST%" disabled={disabled} />
          </div>
          <div className={(showHSN ? "col-span-1" : "col-span-2") + " h-9 flex items-center justify-end px-2 text-sm font-medium tabular-nums"}
               data-testid={`${testidPrefix}-amount-${i}`}>
            {inr(lineAmount)}
          </div>
          <button onClick={() => rm(i)} disabled={disabled}
            className="col-span-1 h-9 text-destructive hover:bg-destructive/10 rounded disabled:opacity-30"
            data-testid={`${testidPrefix}-remove-${i}`}>
            <Trash size={14} className="mx-auto" />
          </button>
        </div>
      );})}
    </div>
  );
}
